import {
	Badge,
	Box,
	Grid,
	GridItem,
	Show,
	Stack,
	useMediaQuery,
} from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { FaCartShopping } from 'react-icons/fa6';
import { useNavigate } from 'react-router';
import { DraftsTopbarIcon, SidebarIcon } from '@/assets/icons';
import ToggleOffThumbIcon from '@/assets/icons/ToggleOffThumbIcon';
import ToggleOnThumbIcon from '@/assets/icons/ToggleOnThumbIcon';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftSwitch from '@/components/Atoms/FaceliftSwitch/FaceliftSwitch';
import TopbarLogo from '@/components/Atoms/TopbarLogo/TopbarLogo';
import MobileDrawer from '@/components/Molecules/MobileDrawer/MobileDrawer';
import NavLinks from '@/components/Molecules/NavLinks/NavLinks';
import QuickActionsDropdown from '@/components/Molecules/QuickActionsDropdown/QuickActionsDropdown';
import TopbarSettings from '@/components/Molecules/TopbarSettings/TopbarSettings';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import { useAppTour } from '@/hooks/useAppTour';
import { useInvoiceCart } from '@/hooks/useInvoiceCart';
import usePermission from '@/hooks/usePermission';
import { selectUser, useAuthStore } from '@/store';
import { getDisplayableImageUrl } from '@/utils/fileUtils';

const Topbar: React.FC = () => {
	const user = useAuthStore(selectUser);
	const navigate = useNavigate();
	const { hasPermission } = usePermission();

	const { colorMode, toggleColorMode } = useColorMode();
	const { cartedInvoices } = useInvoiceCart();
	const darkMode = colorMode === 'dark';
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [drawerExpandedItems, setDrawerExpandedItems] = useState<string[]>([]);
	// const [isHovered, setIsHovered] = useState(false);
	const [isSidebarMode] = useMediaQuery(['(max-width: 1030px)']);
	const [isMidViewport] = useMediaQuery([
		'(min-width: 1030px) and (max-width: 1200px)',
	]);
	const [isSmallScreen] = useMediaQuery(['(max-width: 1030px)']);
	const hoverTimeoutRef = useRef<number | null>(null);

	// Collapse by default (desktop); expand when hovered
	const isCollapsed = false;

	// const handleMouseEnter = () => {
	// 	if (hoverTimeoutRef.current) {
	// 		clearTimeout(hoverTimeoutRef.current);
	// 		hoverTimeoutRef.current = null;
	// 	}
	// 	setIsHovered(true);
	// };

	// const handleMouseLeave = () => {
	// 	// Add a small delay before collapsing again
	// 	hoverTimeoutRef.current = window.setTimeout(() => {
	// 		setIsHovered(false);
	// 	}, 300);
	// };

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);

	// Listen for tour drawer open/close events
	useEffect(() => {
		const handleTourOpenDrawer = (event: CustomEvent<{ items?: string[] }>) => {
			if (isSidebarMode) {
				setIsDrawerOpen(true);
				if (event.detail?.items) {
					setDrawerExpandedItems(event.detail.items);
				}
			}
		};

		const handleTourCloseDrawer = () => {
			if (isSidebarMode) {
				setIsDrawerOpen(false);
				setDrawerExpandedItems([]);
			}
		};

		window.addEventListener('tour:openDrawer', handleTourOpenDrawer as EventListener);
		window.addEventListener('tour:closeDrawer', handleTourCloseDrawer as EventListener);
		return () => {
			window.removeEventListener('tour:openDrawer', handleTourOpenDrawer as EventListener);
			window.removeEventListener('tour:closeDrawer', handleTourCloseDrawer as EventListener);
		};
	}, [isSidebarMode]);

	const cartButtonRef = useRef<HTMLButtonElement | null>(null);

	useAppTour({
		elements: {
			cartButtonRef: cartButtonRef.current,
			InvoicesShoppingCartIconRef: cartButtonRef.current,
		},
	});
	return (
		<Box
			position="sticky"
			top={0}
			zIndex={500}
			background="brand.solidGradient"
			boxShadow="sm"
			transition="all 0.3s ease-in-out"
			height={isCollapsed ? '10' : isMidViewport ? '16' : '14'}
			// onMouseEnter={handleMouseEnter}
			// onMouseLeave={handleMouseLeave}
		>
			<Grid
				templateColumns={isSidebarMode ? "auto 1fr auto" : "repeat(5, 1fr)"}
				minH={isCollapsed ? 10 : isMidViewport ? 20 : 16}
				pb={isCollapsed ? 4 : isMidViewport ? 8 : 1.5}
				transition="all 0.3s ease-in-out"
				px={{ base: 2, sm: 8 }}
				gap={isSidebarMode ? 2 : 0}
			>
				<Show when={isSidebarMode}>
					<GridItem>
						<Stack alignItems="flex-start">
							<FaceliftIconButton
								aria-label="Open navigation menu"
								variant="ghost"
								color="white"
								bg="transparent"
								_hover={{ bg: 'transparent' }}
								onClick={() => setIsDrawerOpen(true)}
								mt={2}
							>
								<SidebarIcon height={12} />
							</FaceliftIconButton>
						</Stack>
					</GridItem>
				</Show>
				<GridItem
					colSpan={isSidebarMode ? undefined : 1}
					display="flex"
					alignItems="center"
					justifyContent={isSidebarMode ? 'center' : 'flex-start'}
					flex={isSidebarMode ? 1 : undefined}
				>
					<Box
						flexShrink={0}
						ml={isMidViewport ? -2 : isCollapsed ? 0 : 6}
						mr={isMidViewport ? -2 : isCollapsed ? 0 : 2}
					>
						<TopbarLogo
							isCollapsed={isCollapsed}
							view={isMidViewport ? 'mid' : 'desktop'}
						/>
					</Box>
				</GridItem>

				<Show when={!isSidebarMode}>
					<GridItem
						colSpan={3}
						display="flex"
						alignItems="center"
						justifyContent="center"
						gap={0}
					>
						<NavLinks 
							isCollapsed={isCollapsed}
						/>
						<QuickActionsDropdown 
							isCollapsed={isCollapsed}
						/>
					</GridItem>
				</Show>

				<GridItem
					colSpan={isSidebarMode ? undefined : 1}
					display="flex"
					alignItems="center"
					justifyContent="flex-end"
					flexShrink={0}
					minW={isSidebarMode ? "fit-content" : undefined}
				>
					<Show when={hasPermission(['Invoice:Pay'])}>
						<FaceliftIconButton
							ref={cartButtonRef}
							aria-label="Cart"
							bg="transparent"
							size={'lg'}
							color="white"
							borderRadius="full"
							_hover={{
								bg: colors.hoverBg,
								transition: 'opacity 0.2s ease-in-out',
							}}
							css={{
								'& svg': {
									width: '20px !important',
									height: '22px !important',
								},
							}}
					onClick={() => {
						navigate('/invoice-cart');
					}}
					mr={1}
				>
							<Show when={!!cartedInvoices.length}>
								<Badge
									position="absolute"
									top="-2px"
									right="-2px"
									borderRadius="full"
									bg="red.500"
									color="white"
									fontSize="10px"
									minW="18px"
									h="18px"
									display="flex"
									alignItems="center"
									justifyContent="center"
									fontWeight="bold"
									px={1}
									zIndex={1}
								>
									{cartedInvoices.length}
								</Badge>
							</Show>
							<FaCartShopping />
						</FaceliftIconButton>
					</Show>

					<Show when={hasPermission(['Draft:Visualize'])}>
						<FaceliftIconButton
							aria-label="Drafts"
							bg="transparent"
							size={'lg'}
							w="12"
							h="12"
							minW="12"
							color="white"
							borderRadius="full"
							_hover={{
								bg: colors.hoverBg,
								transition: 'opacity 0.2s ease-in-out',
							}}
							onClick={() => {
								navigate('/drafts');
							}}
							mr={isSmallScreen ? 1 : 4}
						>
							<DraftsTopbarIcon color="white" width={30} height={30} />
						</FaceliftIconButton>
					</Show>

				<Show when={!isSmallScreen}>
					<FaceliftSwitch
						checked={darkMode}
						onCheckedChange={toggleColorMode}
						unCheckedThumbIcon={<ToggleOffThumbIcon />}
						checkedThumbIcon={<ToggleOnThumbIcon />}
						mr={5}
						border="1px solid"
						borderColor="gray.200"
						borderRadius="full"
					/>
				</Show>
				
					<TopbarSettings
						name={
							user?.userInformation?.firstName +
							' ' +
							user?.userInformation?.lastName
						}
						previewUrl={getDisplayableImageUrl(
							user?.userInformation?.profileImage || '',
						)}
					hideAvatar={isSmallScreen}
					/>
				</GridItem>
			</Grid>

			{/* Mobile Drawer */}
			<MobileDrawer
				isOpen={isDrawerOpen && isSidebarMode}
				onClose={() => setIsDrawerOpen(false)}
				setExpandedItemsForTour={setDrawerExpandedItems}
				expandedItemsFromTour={drawerExpandedItems}
			userName={
				user?.userInformation?.firstName +
				' ' +
				user?.userInformation?.lastName
			}
			userProfileImage={getDisplayableImageUrl(
				user?.userInformation?.profileImage || '',
			)}
			colorMode={colorMode}
			onToggleColorMode={toggleColorMode}
			/>
		</Box>
	);
};

export default Topbar;
